import React, { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useUserContext } from '../../context/users/UserContext'
import { validatePermissions } from '../../context/users/UserActions'
import HostCanvas from './HostCanvas'
import GuestCanvas from './GuestCanvas'
import GuestWelcome from './GuestWelcome'
import SessionError from './SessionError'
import { add } from '../../context/users/UserActions'
import { messageForGuestSessionAccess } from '../../utils/sessionGuestMessages'

const EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/

// Canvas.js ahora actúa como router principal
export default function Canvas() {
  const { session, authenticatedUser, isAuthenticated, authenticateUser, setToken, setAlert } = useUserContext()
  const [userName, setUsername] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [validEmail, setValidEmail] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const params = useParams()
  const location = useLocation()
  
  // Detectar si estamos en la ruta del anfitrión
  const isHostRoute = location.pathname.startsWith('/host/sesion/')

  const currentSsn = localStorage.getItem('alleanza_currentSession')
  const currentSession = currentSsn ? JSON.parse(currentSsn) : { sessionCode: params.id }
  const [sameSession, setSameSession] = useState(currentSession.sessionCode === params.id)

  const handleChangeEmail = (e) => {
    setUserEmail(e.target.value)
    if (e.target.value === '') {
      setValidEmail(true)
    } else {
      setValidEmail(EMAIL_REGEX.test(e.target.value))
    }
  }

  const handleChangeName = (e) => setUsername(e.target.value)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    if (!validEmail) {
      setAlert({
        message: 'Por favor ingresa el correo electrónico con formato válido.',
        msgType: 'warning'
      })
      setIsLoading(false)
      return
    }

    if (userName === '' || userEmail === '') {
      setAlert({
        message: 'Por favor ingresa todos los datos requeridos',
        msgType: 'warning'
      })
      setIsLoading(false)
      return
    }

    try {
      let userData = await add({
        path: 'guest/registry',
        data: {
          userEmail: userEmail.trim(),
          userName,
          sessionId: params.id
        },
        token: ''
      })

      if (Object.prototype.hasOwnProperty.call(userData, 'token')) {
        const expiry = new Date().getTime() + (4 * 1000 * 60 * 60)
        const data = {
          value: userData.token,
          userId: userData.userId,
          pendingPlan: userData.pendingPlan,
          sessionCode: userData.sessionCode,
          expiry,
          permissions: userData?.permissions || [],
        }
        localStorage.setItem('alleanza_jwtoken', JSON.stringify(data))
        authenticateUser(userData)
        setToken(userData.token)
        window.location.reload()
        setSameSession(true)
        localStorage.setItem('alleanza_currentSession', JSON.stringify({ sessionCode: params.id }))
      } else {
        setAlert({
          message:
            messageForGuestSessionAccess(userData.message, userData) ||
            'No se pudo unirse a la sesión.',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al unirse a la sesión. Por favor, inténtalo de nuevo.',
        msgType: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Decidir qué componente mostrar basado en autenticación y permisos
  const renderComponent = () => {
    // Si estamos en la ruta del anfitrión, mostrar HostCanvas directamente
    if (isHostRoute) {
      return <HostCanvas />
    }

    // Si no está autenticado, mostrar formulario de bienvenida
    if (!isAuthenticated || !sameSession) {
      return (
        <GuestWelcome
          sessionName={session?.sessionName || 'Sesión Colaborativa'}
          sessionDescription={session?.description}
          userName={userName}
          userEmail={userEmail}
          onUserNameChange={handleChangeName}
          onUserEmailChange={handleChangeEmail}
          onJoinSession={handleSubmit}
          isLoading={isLoading}
        />
      )
    }

    // Si está autenticado, verificar si es anfitrión o invitado
    const isHost = validatePermissions(authenticatedUser?.permissions, 'isHost')
    
    if (isHost) {
      return <HostCanvas />
    } else {
      return <GuestCanvas />
    }
  }

  return (
    <div className="canvas-router">
      {renderComponent()}
    </div>
  )
}